import { Box, ButtonBase, Container, styled } from '@mui/material';
import { useState } from 'react';

import { routes } from '@/assets/constants/routes-no.ts';
import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWinner, CompetitionWithConfig } from '@/lib/api/requests.ts';
import useBrainSession from '@/services/BrainSession/useBrainSession.ts';
import { NumberCircle } from '@/ui/NumberCircle.tsx';
import { NumberedList, NumberedListItem } from '@/ui/NumberedList.tsx';

import QuestionDialog from '../_assets/question-dialog.svg?react';
import { CTA } from '../_components/CTA.tsx';
import { useHasEnded } from '../_components/useHasEnded.ts';

const Root = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  width: '100%',
  isolation: 'isolate',
}));

export const TopSection = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
}) => {
  return (
    <>
      <Root>
        <Background />

        <Container maxWidth="xl" sx={{ pt: { xs: 4, md: 10 }, pb: { xs: 8, md: 12 } }}>
          <Content activeCompetition={props.activeCompetition} winners={props.winners} />
        </Container>
      </Root>

      <Disclaimer />
    </>
  );
};

const Background = () => (
  <div className="absolute inset-0 isolate -z-10 flex flex-col overflow-hidden bg-gradient-to-b from-[#072453] to-[#254780]"></div>
);

const Disclaimer = () => (
  <div className="bg-white pt-4">
    <Container maxWidth="md">
      <div className="text-center typography-v1-body4 text-balance">
        <LoanExampleText />
      </div>
    </Container>
  </div>
);

const Content = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
}) => {
  const ended = useHasEnded();
  const [isOpen, setIsOpen] = useState(false);
  const { toggleChat } = useBrainSession();

  return (
    <div className="text-white">
      <div className="flex flex-col items-center gap-10 lg:flex-row">
        <div className="flex-6">
          <h1 className="mb-4 text-4xl leading-tight font-bold text-balance sm:text-5xl xl:text-6xl">
            Gjett om du kan få lavere rente med Scoopr
          </h1>
          <Box
            component="p"
            sx={{
              mb: { xs: 3, md: 4, lg: 3 },
              maxWidth: { xs: 370, sm: 600, md: 750, xl: 800 },
              fontWeight: 400,
            }}
            className="typography-v1-h4 text-balance"
          >
            Gjetter du riktig, er du med i trekningen om å få nedbetalt 25 000 kr på din
            forbruksgjeld – vi trekker 10 vinnere!
          </Box>
          <p className="mb-2 typography-v1-h4">For å delta:</p>

          <NumberedList
            sx={{ mb: 2 }}
            renderNumber={(props) => (
              <NumberCircle
                sx={{
                  fontSize: 14,
                  height: 20,
                  backgroundColor: '#FBFDFF',
                  color: '#031839',
                }}
                {...props}
              />
            )}
            renderLabel={(props) => <p {...props} className="typography-v1-body3" />}
          >
            <NumberedListItem>Logg inn med BankID</NumberedListItem>
            <NumberedListItem>Gjett om scoopr kan gi deg lavere rente</NumberedListItem>
            <NumberedListItem>Se fasit og delta</NumberedListItem>
          </NumberedList>

          {ended && (
            <p className="mb-6 typography-v1-h4">
              <span>Denne trekningen er avsluttet. </span>
              <ButtonBase
                onClick={() => setIsOpen(true)}
                sx={{
                  color: '#74C6FD',
                  textDecoration: 'underline',
                  mb: 0.5,
                  fontWeight: 700,
                  ml: 1,
                }}
              >
                Se vinneren her
              </ButtonBase>
            </p>
          )}

          <Box sx={{ mb: 3 }}>
            <a
              href={routes.JULY_2025_COMPETITION_TERMS}
              style={{ color: 'inherit' }}
              className="underline"
              target="_blank"
            >
              <p className="typography-v1-body3">Vilkår og betingelser for deltagelse</p>
            </a>
          </Box>

          <CTA
            activeCompetition={props.activeCompetition}
            onClick={() => {
              sendUserPortalEvent('jul25_competition_cta_top_clicked');
            }}
          />

          {props.winners && (
            <CompetitionWinnersDialog
              open={isOpen}
              onClose={() => setIsOpen(false)}
              winners={props.winners}
            />
          )}
        </div>

        <div className="hidden flex-5 lg:flex">
          <div className="relative isolate w-full">
            <svg
              width="837"
              height="769"
              viewBox="0 0 837 769"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="absolute top-0 right-0 -z-10 translate-x-1/3 -translate-y-1/3"
            >
              <g opacity="0.6" filter="url(#filter0_f_19295_42815)">
                <ellipse
                  cx="418.54"
                  cy="384.496"
                  rx="222.5"
                  ry="178.5"
                  transform="rotate(-20.2569 418.54 384.496)"
                  fill="#18A0FB"
                  fillOpacity="0.53"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_19295_42815"
                  x="0.788086"
                  y="0.121948"
                  width="835.504"
                  height="768.748"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageFix"
                    result="shape"
                  />
                  <feGaussianBlur stdDeviation="100" result="effect1_foregroundBlur_19295_42815" />
                </filter>
              </defs>
            </svg>

            <QuestionDialog
              className="h-auto w-full"
              onClick={() => {
                if (!ended) {
                  sendUserPortalEvent('jul25_competition_cta_image_clicked');
                  toggleChat({ goal: 'july_2025_campaign', step: 'init' });
                }
              }}
            />

            <svg
              width="837"
              height="769"
              viewBox="0 0 837 769"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="pointer-events-none absolute bottom-0 left-0 -z-10 -translate-x-1/2 translate-y-1/2"
            >
              <g opacity="0.6" filter="url(#filter0_f_19295_42811)">
                <ellipse
                  cx="418.54"
                  cy="384.496"
                  rx="222.5"
                  ry="178.5"
                  transform="rotate(-20.2569 418.54 384.496)"
                  fill="#18A0FB"
                  fillOpacity="0.34"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_19295_42811"
                  x="0.788086"
                  y="0.121948"
                  width="835.504"
                  height="768.748"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageFix"
                    result="shape"
                  />
                  <feGaussianBlur stdDeviation="100" result="effect1_foregroundBlur_19295_42811" />
                </filter>
              </defs>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};
